import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box } from '@mui/material';
import { useDataProvider, useGetIdentity, useRefresh } from 'react-admin';
import { DEFAULTS } from '../../constants';
import { toast } from 'react-toastify';
import { CreateLocationDialogProps } from '../../utils/DataTypes';

const CreateLocationDialog: React.FC<CreateLocationDialogProps> = ({ isOpen, onClose, onSave, selectedClient }) => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();

  const [locationNumber, setLocationNumber] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationDisplayNumber, setLocationDisplayNumber] = useState('');
  const [isExistingLocation, setIsExistingLocation] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const errorMsg = isExistingLocation
    ? `Location ${locationNumber.trim()} already exists for this client. Please edit the existing record instead.`
    : '';

  const handleValidateLocationNumber = async () => {
    setIsExistingLocation(false);
    const cleanNumber = locationNumber.trim();

    if (!selectedClient || !cleanNumber) return false;

    setIsValidating(true);
    try {
      const { data: duplicateData } = await dataProvider.getList('ValidateLocations', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          billing_client_number: selectedClient.billing_client_number,
          location_numbers: [cleanNumber],
        },
      });

      const found = duplicateData.length > 0;
      setIsExistingLocation(found);
      return found;
    } catch (error) {
      toast.error('Location validation failed');
      return true;
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async () => {
    const duplicateFound = await handleValidateLocationNumber();
    if (duplicateFound) return;

    const cleanNumber = locationNumber.trim();
    const cleanName = locationName.trim();
    const cleanDisplay = locationDisplayNumber.trim();

    const payload = {
      import_mode: 'append',
      billing_client_name: selectedClient?.billing_client_name || '',
      billing_client_number: selectedClient?.billing_client_number || '',
      locations: [
        {
          location_number: cleanNumber,
          location_name: cleanName,
          location_display_number: cleanDisplay,
          created_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
          last_modified_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
        },
      ],
    };

    try {
      const { data: result } = await dataProvider.create('BulkAddLocations', {
        data: payload,
      });

      toast.success(result.message || 'Location created successfully!');
      refresh();
      onClose();
      onSave(payload);
    } catch (error: any) {
      toast.error(`Create failed: ${error.message || 'Server error'}`);
    }
  };

  const isSaveDisabled =
    !selectedClient || !locationNumber.trim() || !locationName.trim() || isExistingLocation || isValidating;

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create New Location Crosswalk</DialogTitle>
      <DialogContent>
        <Box display="flex" gap={2} mb={1} mt={1}>
          <TextField
            label="Billing Client Name"
            value={selectedClient?.billing_client_name || ''}
            fullWidth
            variant="standard"
            disabled
          />
          <TextField
            label="Client Number"
            value={selectedClient?.billing_client_number || ''}
            variant="standard"
            disabled
            sx={{ width: '200px' }}
          />
        </Box>
        <TextField
          required
          margin="dense"
          label="Location Number"
          type="text"
          fullWidth
          variant="standard"
          value={locationNumber}
          error={isExistingLocation}
          helperText={errorMsg}
          onChange={(e) => {
            setLocationNumber(e.target.value);
            setIsExistingLocation(false);
          }}
          onBlur={handleValidateLocationNumber}
        />
        <TextField
          margin="dense"
          label="Display Number"
          type="text"
          fullWidth
          variant="standard"
          value={locationDisplayNumber}
          onChange={(e) => setLocationDisplayNumber(e.target.value)}
        />
        <TextField
          required
          margin="dense"
          label="Location Name"
          type="text"
          fullWidth
          variant="standard"
          value={locationName}
          onChange={(e) => setLocationName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isValidating}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaveDisabled}>
          {isValidating ? 'Validating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLocationDialog;
